// Copyright (c) 2023 embyt GmbH. All rights reserved.
// Author: Marc Planeck

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, WrappedComponentProps, injectIntl } from "react-intl";
import { Button, Modal } from "react-bootstrap";

import { IState } from "states/states";
import { IAction, ActionType, raiseAction, IActionTypeSignUp } from "actions";
import { DataState, UserStateAWSError } from "types";
import { tabClickHandler } from "guiutils";
import { UserState } from "states/schedulerstates";
import { isSet } from "utils";
import { UserRole } from "graphql/api";
import LoginErrorStatus from "./LoginErrorStatus";

declare const IS_CLOUD: boolean;

interface IUserSignUpExtProps {
  adminMode?: boolean;
}
interface IUserSignUpProps {
  signUpDataState: DataState;
  userEmail: string | undefined;
  signUpErrorFeedback: UserStateAWSError | undefined;
  willSignUp: boolean;
  isRootUser: boolean;
}

const mapStateToProps = (state: IState, extProps: IUserSignUpExtProps) => {
  const curUser = state.site.userList?.find((curItem) => curItem.secId === state.site.user?.secId);

  const props: IUserSignUpProps = {
    signUpDataState: state.scheduler.loginDataState,
    userEmail: state.gui.login.email,
    willSignUp: state.scheduler.userState === UserState.SINGING_UP || extProps.adminMode === true,
    signUpErrorFeedback: state.gui.login.userStateAWSFeedback,
    isRootUser: curUser?.role === UserRole.root,
  };
  return props;
};

interface IUserSignUpState {
  username: string;
  password: string;
  verifyPassword: string;
  name: string;
  company: string;
  confirmationCode: string;
  showMismatchWaring: boolean;
  showMismatchError: boolean;
}

interface IUserSignUpDispatch {
  onLogin: (username: string) => void;
  onSignUpClick: (state: IUserSignUpState) => void;
  onSignUpResendClick: (state: IUserSignUpState) => void;
  onSignUpConfirmClick: (state: IUserSignUpState) => void;
  onShowSignUpConfirmClick: (username: string) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IUserSignUpDispatch = {
    onLogin: (username: string) => {
      dispatch(raiseAction(ActionType.ACT_BACK_TO_LOGIN, username));
    },
    onSignUpClick: (state: IUserSignUpState) => {
      const payload: IActionTypeSignUp = {
        username: state.username,
        password: state.password,
        company: state.company,
        realName: state.name,
        keepErrorState: false,
      };
      dispatch(raiseAction(ActionType.ACT_SIGNUP_REQ, payload));
    },
    onSignUpResendClick: (state: IUserSignUpState) => {
      const payload: IActionTypeSignUp = {
        username: state.username,
        password: "",
        keepErrorState: false,
      };
      dispatch(raiseAction(ActionType.ACT_SIGNUP_RESEND_REQ, payload));
    },
    onSignUpConfirmClick: (state: IUserSignUpState) => {
      const payload: IActionTypeSignUp = {
        username: state.username,
        password: state.password,
        code: state.confirmationCode,
        keepErrorState: false,
      };
      dispatch(raiseAction(ActionType.ACT_SIGNUP_CONFIRM_REQ, payload));
    },
    onShowSignUpConfirmClick: (username: string) => {
      const payload: IActionTypeSignUp = {
        username,
        password: "",
        keepErrorState: false,
      };
      dispatch(raiseAction(ActionType.ACT_SIGNUP_CONFIRM, payload));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({});

class UserSignUpComponent extends React.Component<
  IUserSignUpProps & IUserSignUpExtProps & IUserSignUpDispatch & WrappedComponentProps,
  IUserSignUpState
> {
  state: IUserSignUpState = {
    username: IS_CLOUD ? this.props.userEmail ?? "" : "operator",
    password: "",
    verifyPassword: "",
    name: "",
    company: "",
    confirmationCode: "",
    showMismatchWaring: false,
    showMismatchError: false,
  };

  public render() {
    return (
      <>
        {this.renderModal()}
        <form
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <table>
            <tbody>
              {this.props.willSignUp && (
                <>
                  <tr>
                    <td>
                      <FormattedMessage id="Name" />
                    </td>
                    <td>
                      <input
                        type="text"
                        name="name"
                        value={this.state.name}
                        autoFocus
                        disabled={this.props.signUpDataState === DataState.SAVE_REQ}
                        onChange={(event) => {
                          this.setState({ name: event?.currentTarget.value });
                        }}
                      />
                      <div className="mb-2"></div>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      {this.props.isRootUser ? (
                        <input
                          type="text"
                          name="company"
                          value={this.state.company}
                          disabled={this.props.signUpDataState === DataState.SAVE_REQ}
                          onChange={(event) => {
                            this.setState({ company: event?.currentTarget.value });
                          }}
                        />
                      ) : (
                        <>{this.state.company}</>
                      )}
                    </td>
                  </tr>
                </>
              )}
              {this.props.willSignUp ||
              !isSet(this.props.userEmail) ||
              this.props.userEmail === "" ? (
                <>
                  <tr>
                    <td>
                      <FormattedMessage id="Email" />
                    </td>
                    <td>
                      <input
                        type="text"
                        name="username"
                        className={
                          this.isUsernameValid() && this.state.username.length >= 0
                            ? undefined
                            : "input-error"
                        }
                        value={this.state.username}
                        disabled={this.props.signUpDataState === DataState.SAVE_REQ}
                        onChange={(event) => {
                          this.setState({ username: event?.currentTarget.value });
                        }}
                      />
                      {this.props.signUpDataState === DataState.SAVE_FAIL &&
                        this.props.signUpErrorFeedback === UserStateAWSError.USERNAME_MISSING && (
                          <div className="loginError">
                            <FormattedMessage id="Check your Email Address" />
                          </div>
                        )}
                      <div className="mb-2"></div>
                    </td>
                  </tr>
                </>
              ) : (
                <>
                  <tr>
                    <td colSpan={2}>
                      <FormattedMessage id="Please enter the code you received via email" />
                      <b>{` ${this.state.username} `}</b>
                      {!isSet(this.state.password) || this.state.password === "" ? (
                        <FormattedMessage id="together with your password..." />
                      ) : (
                        <FormattedMessage id="below..." />
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={2}>
                      {IS_CLOUD &&
                        this.props.signUpDataState !== DataState.SAVE_REQ &&
                        this.props.willSignUp === false &&
                        isSet(this.props.userEmail) &&
                        this.props.userEmail !== "" && (
                          <>
                            <div className="loginOptions">
                              <div className="link" style={{ marginTop: 11 }}></div>
                              <span
                                tabIndex={0}
                                onKeyDown={tabClickHandler}
                                className="text-link ms-3"
                                onClick={(event) => {
                                  this.setState({ username: "" });
                                  this.props.onShowSignUpConfirmClick("");
                                }}
                              >
                                <FormattedMessage id="Not your email?" />
                              </span>
                            </div>
                            <div className="mb-4"></div>
                          </>
                        )}
                    </td>
                  </tr>
                </>
              )}
              {this.props.willSignUp ? (
                <>
                  <tr>
                    <td>
                      <FormattedMessage id="New Password" />:
                    </td>
                    <td>
                      <>
                        <input
                          name="password"
                          type="password"
                          className={
                            this.isPasswordValid() && this.state.password.length >= 0
                              ? undefined
                              : "input-error"
                          }
                          value={this.state.password}
                          disabled={this.props.signUpDataState === DataState.SAVE_REQ}
                          onChange={(event) => {
                            this.setState({ password: event?.currentTarget.value });
                          }}
                        />
                        {(this.props.signUpDataState === DataState.SAVE_FAIL ||
                          this.state.showMismatchError) &&
                          this.state.password !== this.state.verifyPassword && (
                            <div className="loginError">
                              <FormattedMessage id="Check your Password" />
                            </div>
                          )}
                        <div className="mb-2"></div>
                      </>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <FormattedMessage id="Verify new Password" />:
                    </td>
                    <td>
                      <>
                        <input
                          name="verifyPassword"
                          type="password"
                          value={this.state.verifyPassword}
                          className={
                            this.isPasswordValid() && this.state.verifyPassword.length >= 0
                              ? undefined
                              : "input-error"
                          }
                          disabled={this.props.signUpDataState === DataState.SAVE_REQ}
                          onChange={(event) => {
                            this.setState({ verifyPassword: event?.currentTarget.value });
                          }}
                        />
                        {(this.props.signUpDataState === DataState.SAVE_FAIL ||
                          this.state.showMismatchError) &&
                          this.state.password !== this.state.verifyPassword && (
                            <div className="loginError">
                              <FormattedMessage id="Check your Password" />
                            </div>
                          )}
                        <div className="mb-2"></div>
                      </>
                    </td>
                  </tr>
                </>
              ) : (
                <>
                  <tr>
                    <td>
                      <FormattedMessage id="Confirmation Code" />:
                    </td>
                    <td>
                      <>
                        <input
                          name="confirmationCode"
                          type="text"
                          value={this.state.confirmationCode}
                          disabled={this.props.signUpDataState === DataState.SAVE_REQ}
                          onChange={(event) => {
                            this.setState({ confirmationCode: event?.currentTarget.value });
                          }}
                        />
                        {this.props.signUpDataState === DataState.SAVE_FAIL && <LoginErrorStatus />}
                        <div className="mb-2"></div>
                      </>
                    </td>
                  </tr>
                  {(!isSet(this.state.password) || this.state.password === "") && (
                    <>
                      <tr>
                        <td>
                          <FormattedMessage id="Password" />:
                        </td>
                        <td>
                          <>
                            <input
                              name="password"
                              type="password"
                              value={this.state.password}
                              disabled={this.props.signUpDataState === DataState.SAVE_REQ}
                              onChange={(event) => {
                                this.setState({ password: event?.currentTarget.value });
                              }}
                            />
                            {this.props.signUpDataState === DataState.SAVE_FAIL &&
                              (this.props.signUpErrorFeedback === UserStateAWSError.LOGIN_FAILED ||
                                this.props.signUpErrorFeedback ===
                                  UserStateAWSError.PASSWORD_MISSING) && (
                                <div className="loginError">
                                  <FormattedMessage id="Check your Password" />
                                </div>
                              )}
                            <div className="mb-2"></div>
                          </>
                        </td>
                      </tr>
                    </>
                  )}
                </>
              )}
              <tr>
                <td colSpan={2}>
                  {IS_CLOUD &&
                    this.props.signUpDataState !== DataState.SAVE_REQ &&
                    this.props.willSignUp === false && (
                      <>
                        <div className="loginOptions">
                          <div className="link" style={{ marginTop: 11 }}></div>
                          <span
                            tabIndex={0}
                            onKeyDown={tabClickHandler}
                            className="text-link ms-3"
                            onClick={(event) => {
                              this.setState({ confirmationCode: "" });
                              this.props.onSignUpResendClick(this.state);
                            }}
                          >
                            <FormattedMessage id="Resend Code..." />
                          </span>
                        </div>
                      </>
                    )}
                </td>
              </tr>
            </tbody>
          </table>
          <div className="mt-3">
            <Button
              type="submit"
              className="me-2"
              variant="success"
              onClick={() => {
                if (this.isPasswordValid() && this.isUsernameValid()) {
                  this.props.willSignUp
                    ? this.signUp()
                    : this.props.onSignUpConfirmClick(this.state);
                } else {
                  // show error-message - modal?
                  console.error("Username or Password not valid");
                  this.setState({ showMismatchWaring: true, showMismatchError: true });
                }
              }}
              disabled={
                this.props.signUpDataState === DataState.SAVE_REQ ||
                !this.isUsernameValid() ||
                !this.isPasswordValid()
              }
            >
              {this.props.willSignUp ? (
                <FormattedMessage id={this.props.adminMode === true ? "Create" : "SignUp"} />
              ) : (
                <FormattedMessage id="Confirm" />
              )}
            </Button>
            {this.props.signUpDataState !== DataState.SAVE_REQ && this.props.adminMode !== true && (
              <Button
                variant="secondary"
                onClick={(event) => this.props.onLogin(this.state.username)}
              >
                <FormattedMessage id="Cancel" />
              </Button>
            )}
          </div>
          {/* {IS_CLOUD &&
            this.props.signUpDataState !== DataState.SAVE_REQ &&
            this.props.willSignUp === true &&
            this.props.adminMode !== true && (
              <>
                <div className="loginOptions">
                  <div className="link" style={{ marginTop: 11 }}></div>
                  <span
                    tabIndex={0}
                    onKeyDown={tabClickHandler}
                    className="text-link ms-3"
                    onClick={(event) => {
                      this.props.onShowSignUpConfirmClick(this.state.username);
                    }}
                  >
                    <FormattedMessage id="Already have a code?" />
                  </span>
                </div>
              </>
            )} */}
        </form>
      </>
    );
  }

  private renderModal() {
    return (
      <Modal
        show={this.state.showMismatchWaring}
        onHide={() => this.setState({ showMismatchWaring: false })}
      >
        <Modal.Header closeButton>
          <FormattedMessage id="Changing User" />
        </Modal.Header>
        <Modal.Body>
          <FormattedMessage id="The two fields for the new password do not match." />{" "}
          <FormattedMessage id="Please enter the same password twice." />
        </Modal.Body>
      </Modal>
    );
  }

  private signUp() {
    if (this.state.password === this.state.verifyPassword) {
      this.props.onSignUpClick(this.state);
    } else {
      this.setState({ showMismatchWaring: true, showMismatchError: true });
    }
  }

  private isUsernameValid() {
    // check for valid RFID number
    if (IS_CLOUD) {
      const regex = new RegExp("^[a-zA-Z0-9\\.\\-_]+@[a-zA-Z0-9.\\-_]+\\.[a-zA-Z0-9]{2,}$");
      return this.state.username !== "" && regex.test(this.state.username); // outputs true
    }
    return true;
  }

  private isPasswordValid() {
    if (IS_CLOUD) {
      // check for email-style!
      return (
        this.state.password !== "" &&
        this.state.password.length > 5 &&
        this.state.password === this.state.verifyPassword
      );
    }
    return true;
  }
}

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(UserSignUpComponent));
